// Admin › Agente de IA e R.A.G › Dúvidas frequentes (WootDash /ia/duvidas — só o perfil Sistema, ai.mark).
import { WOOTDASH_BASE, wootdashAuthHeaders } from '@/api/wootdash'
import type { AiAgentTestResult, AiExtractionRun, AiTestTurn } from '@/services/wootdashAi'

export type AiFaqStatus = 'open' | 'answered' | 'menu' | 'ignored'
export type AiFaqPeriod = 7 | 30 | 90 | 365 | 0

export interface AiFaqFlowCount { flowNumber: number | null; count: number }
export interface AiFaqDocumentLink { id: string; title: string; isActive: boolean }

export interface AiFaqSummary {
  id: string
  question: string
  category: string | null
  status: AiFaqStatus
  /** Conversas no período escolhido. */
  count: number
  /** Conversas no período anterior, do mesmo tamanho (tendência). */
  previousCount: number
  total: number
  perWeek: number
  flows: AiFaqFlowCount[]
  firstSeenAt: string
  lastSeenAt: string
  answer: string | null
  documents: AiFaqDocumentLink[]
  inMenu: boolean
  menuNote: string | null
  menuMarkedAt: string | null
  coveredBy: string | null
  coverage: number | null
  isGap: boolean
  /** Receita de ferramentas MCP salva (null = sem receita). */
  recipe: AiFaqRecipe | null
  /** Fluxos (número) em que a resposta vale pro copiloto. Vazio = todos. */
  flowNumbers: number[]
  /** Item montado no rascunho de um bot (Virou item de menu do bot automático). null = só marcação ou fora do menu. */
  menuLink?: AiFaqBotMenuLink | null
}

export interface AiFaqList {
  items: AiFaqSummary[]
  counts: Record<AiFaqStatus, number>
  days: number
  conversations: number
  documentOptions: AiFaqDocumentLink[]
  latestRun: AiExtractionRun | null
}

export interface AiFaqExample {
  sourceConversationId: string
  conversationId: string | null
  chatwootId: number | null
  question: string
  answer: string | null
  flowNumber: number | null
  rating: number | null
  origin: 'wootdash' | 'chatwoot'
  resolvedAt: string
}

export interface AiFaqDetail { summary: AiFaqSummary; examples: AiFaqExample[] }

export type AiFaqAction = { ok: true; documentId: string | null } | { ok: false; problems: string[] }

export const FAQ_STATUS_LABEL: Record<AiFaqStatus, string> = {
  open: 'Sem resposta', answered: 'Respondida', menu: 'Virou menu', ignored: 'Ignorada',
}

/** Subindo, caindo ou estável em relação ao período anterior (±20% ou menos é estável). Novo = não apareceu antes. */
export function faqTrend(item: Pick<AiFaqSummary, 'count' | 'previousCount'>, days: number): 'new' | 'up' | 'down' | 'flat' | null {
  if (days <= 0) return null
  if (item.previousCount === 0) return item.count > 0 ? 'new' : null
  const ratio = item.count / item.previousCount
  return ratio >= 1.2 ? 'up' : ratio <= 0.8 ? 'down' : 'flat'
}

function headers(token: string, json = false) {
  return { ...wootdashAuthHeaders(token), ...(json ? { 'Content-Type': 'application/json' } : {}) }
}

export async function listAiFaqs(
  token: string, dias: AiFaqPeriod, situacao: AiFaqStatus | null, fluxo: number | null, busca: string,
): Promise<{ data: AiFaqList | null; problem: string | null }> {
  const params = new URLSearchParams({ dias: String(dias) })
  if (situacao) params.set('situacao', situacao)
  if (fluxo !== null) params.set('fluxo', String(fluxo))
  if (busca.trim()) params.set('busca', busca.trim())
  try {
    const res = await fetch(`${WOOTDASH_BASE}/ia/duvidas?${params}`, { headers: headers(token) })
    if (res.status === 401 || res.status === 403) return { data: null, problem: 'Só o perfil Sistema vê as dúvidas frequentes.' }
    return res.ok ? { data: await res.json(), problem: null } : { data: null, problem: `Erro ${res.status} no servidor.` }
  } catch {
    return { data: null, problem: 'Não foi possível falar com o servidor.' }
  }
}

export async function getAiFaq(token: string, id: string, dias: AiFaqPeriod): Promise<AiFaqDetail | null> {
  try {
    const res = await fetch(`${WOOTDASH_BASE}/ia/duvidas/${id}?dias=${dias}`, { headers: headers(token) })
    return res.ok ? await res.json() : null
  } catch {
    return null
  }
}

async function action(token: string, method: 'PUT' | 'POST', path: string, body: unknown): Promise<AiFaqAction> {
  try {
    const res = await fetch(`${WOOTDASH_BASE}/ia/duvidas/${path}`, { method, headers: headers(token, true), body: JSON.stringify(body) })
    if (res.status === 204) return { ok: true, documentId: null }
    if (res.ok) {
      const data = await res.json().catch(() => null)
      return { ok: true, documentId: data?.documentId ?? null }
    }
    if (res.status === 404) return { ok: false, problems: ['Dúvida não encontrada — pode ter sido juntada com outra. Atualize a lista.'] }
    if (res.status === 401 || res.status === 403) return { ok: false, problems: ['Sem permissão (só o perfil Sistema).'] }
    const data = await res.json().catch(() => null)
    return { ok: false, problems: data?.problems?.length ? data.problems : [`Erro ${res.status} no servidor.`] }
  } catch {
    return { ok: false, problems: ['Não foi possível falar com o servidor.'] }
  }
}

export const reviewAiFaq = (token: string, id: string, duvida: string, resposta: string | null, documentos: string[], fluxos: number[] = []) =>
  action(token, 'PUT', id, { duvida, resposta, documentos, fluxos })
export const createAiFaqDocument = (token: string, id: string, titulo: string, conteudo: string, etiquetas: string[]) =>
  action(token, 'POST', `${id}/documento`, { titulo, conteudo, etiquetas })
export const markAiFaqMenu = (token: string, id: string, noMenu: boolean, nota: string | null) =>
  action(token, 'POST', `${id}/menu`, { noMenu, nota })
/** after: closing (Algo mais?), handoff, end ou goto:{id}. botText vazio = a resposta da dúvida. */
export interface AiFaqBotMenuLink {
  botId: string
  menuBlockId: string
  buttonText: string
  description: string | null
  flowId: string | null
  after: string
  botText: string | null
  optionId?: string | null
  groupId?: string | null
}

/** Põe/atualiza o item no RASCUNHO do bot. ok traz a nota (onde ficou); senão, os problemas. */
export async function linkAiFaqBotMenu(token: string, id: string, link: AiFaqBotMenuLink): Promise<{ ok: true; note: string } | { ok: false; problems: string[] }> {
  try {
    const res = await fetch(`${WOOTDASH_BASE}/ia/duvidas/${id}/menu-bot`, { method: 'PUT', headers: headers(token, true), body: JSON.stringify(link) })
    const data = await res.json().catch(() => null)
    if (res.ok) return { ok: true, note: data?.note ?? '' }
    if (res.status === 401 || res.status === 403) return { ok: false, problems: ['Sem permissão: precisa marcar dúvidas e editar bots.'] }
    return { ok: false, problems: data?.problems?.length ? data.problems : [`Erro ${res.status} no servidor.`] }
  } catch {
    return { ok: false, problems: ['Não foi possível falar com o servidor.'] }
  }
}

/** Tira o item do rascunho do bot e desmarca a dúvida. */
export async function unlinkAiFaqBotMenu(token: string, id: string): Promise<boolean> {
  try {
    const res = await fetch(`${WOOTDASH_BASE}/ia/duvidas/${id}/menu-bot`, { method: 'DELETE', headers: headers(token) })
    return res.ok
  } catch {
    return false
  }
}

export const ignoreAiFaq = (token: string, id: string, ignorar: boolean) => action(token, 'POST', `${id}/ignorar`, { ignorar })
/** Juntar as outras dúvidas nesta. duvida = novo texto da principal (a mesclagem inteligente propõe um). */
export const mergeAiFaqs = (token: string, id: string, duvidas: string[], duvida: string | null = null) => action(token, 'POST', `${id}/juntar`, { duvidas, duvida })

export async function regroupAiFaqs(token: string): Promise<number | null> {
  try {
    const res = await fetch(`${WOOTDASH_BASE}/ia/duvidas/reagrupar`, { method: 'POST', headers: headers(token) })
    return res.ok ? (await res.json()).grouped : null
  } catch {
    return null
  }
}

export async function extractAiFaqs(token: string): Promise<{ queued: number; run: AiExtractionRun | null } | null> {
  try {
    const res = await fetch(`${WOOTDASH_BASE}/ia/duvidas/extrair`, { method: 'POST', headers: headers(token) })
    return res.ok ? await res.json() : null
  } catch {
    return null
  }
}

/* ── Entrega 1: pesquisa na internet, simulador e "Testar com os exemplos" ── */

/** O que está na tela (ainda sem salvar). null nas chamadas = usar a versão salva. */
export interface AiFaqDraft { duvida: string; resposta: string | null; documentos: string[]; receita?: AiFaqRecipe | null }

export interface AiFaqWebSource { title: string; url: string }

export interface AiFaqResearchResult {
  success: boolean
  problem: string | null
  draft: string | null
  sources: AiFaqWebSource[]
  queries: string[]
  tokens: number | null
}

export type AiFaqVerdict = 'resolveria' | 'parcial' | 'transferiria' | 'erraria'

export interface AiFaqExampleTest {
  question: string
  reply: string | null
  verdict: AiFaqVerdict | null
  serious: boolean
  reason: string | null
  problem: string | null
}

export interface AiFaqExamplesResult {
  success: boolean
  problem: string | null
  items: AiFaqExampleTest[]
  counts: Partial<Record<AiFaqVerdict, number>>
}

export const VERDICT_LABEL: Record<AiFaqVerdict, string> = {
  resolveria: 'Resolveria', parcial: 'Parcial', transferiria: 'Transferiria', erraria: 'Erraria',
}

async function post<T>(token: string, path: string, body: unknown, failed: (problem: string) => T, valid: (data: unknown) => boolean): Promise<T> {
  try {
    const res = await fetch(`${WOOTDASH_BASE}/ia/duvidas/${path}`, { method: 'POST', headers: headers(token, true), body: JSON.stringify(body) })
    if (res.status === 401 || res.status === 403) return failed('Sem permissão (só o perfil Sistema).')
    const data = await res.json().catch(() => null)
    return res.ok && valid(data) ? data as T : failed(`Erro ${res.status} no servidor.`)
  } catch {
    return failed('Não foi possível falar com o servidor.')
  }
}

const hasSuccess = (data: unknown) => typeof (data as { success?: unknown } | null)?.success === 'boolean'

/** Responder falando: o texto da gravação (transcrito pela Vertex; arrumar = tirar vícios de fala e pontuar). Nada é salvo. */
export interface AiFaqDictationResult { success: boolean; problem: string | null; text: string | null; tokens: number | null }
export async function dictateAiFaq(token: string, audio: Blob, arrumar: boolean, campo: 'pergunta' | 'resposta'): Promise<AiFaqDictationResult> {
  const failed = (problem: string): AiFaqDictationResult => ({ success: false, problem, text: null, tokens: null })
  const form = new FormData()
  form.append('audio', audio, `ditado.${(audio.type.split('/')[1] ?? 'webm').split(';')[0]}`)
  try {
    const res = await fetch(`${WOOTDASH_BASE}/ia/duvidas/ditar?arrumar=${arrumar}&campo=${campo}`, { method: 'POST', headers: wootdashAuthHeaders(token), body: form })
    if (res.status === 401 || res.status === 403) return failed('Sem permissão (só o perfil Sistema).')
    const data = await res.json().catch(() => null)
    return res.ok && hasSuccess(data) ? data as AiFaqDictationResult : failed(`Erro ${res.status} no servidor.`)
  } catch {
    return failed('Não foi possível falar com o servidor.')
  }
}

export const researchAiFaq = (token: string, duvida: string, respostaAtual: string | null) =>
  post<AiFaqResearchResult>(token, 'pesquisar', { duvida, respostaAtual },
    problem => ({ success: false, problem, draft: null, sources: [], queries: [], tokens: null }), hasSuccess)

export const simulateAiFaq = (token: string, id: string, agenteId: string, rascunho: AiFaqDraft | null, mensagem: string, historico: AiTestTurn[], opcoes: AiFaqRunOptions = { chamarDeVerdade: true, fingidos: {} }) =>
  post<AiAgentTestResult>(token, `${id}/simular`, { agenteId, rascunho, mensagem, historico, ...opcoes },
    problem => ({
      success: false, problem, stage: 'network', reply: null, sentMessage: null, sources: [], model: null, latencyMs: null, inputTokens: null, outputTokens: null,
      memoryUsed: 0, note: null, disclosure: null, searchMode: null, matches: null,
    }), hasSuccess)

export const testAiFaqExamples = (token: string, id: string, agenteId: string, rascunho: AiFaqDraft | null) =>
  post<AiFaqExamplesResult>(token, `${id}/testar-exemplos`, { agenteId, rascunho },
    problem => ({ success: false, problem, items: [], counts: {} }), hasSuccess)

/* ── Entrega 2: receita de ferramentas MCP ── */

export interface McpToolParam { name: string; type: string; required: boolean; description: string | null }
/** sideEffect = muda algo fora do sistema (só consulta get_/retorna_ é livre): no teste, só com retorno fingido. */
export interface McpTool { name: string; description: string; params: McpToolParam[]; sideEffect: boolean }
export interface AiFaqToolCatalog { tools: McpTool[]; problem: string | null }

/** ask = perguntar ao contato; fixed = valor fixo; step = retorno de um passo anterior; none = não usar (opcional). */
export type AiFaqInputSource = 'ask' | 'fixed' | 'step' | 'none'

export interface AiFaqRecipeInput {
  name: string
  source: AiFaqInputSource
  description?: string | null
  value?: string | null
  stepId?: string | null
  path?: string | null
}

export interface AiFaqRecipeStep { id: string; tool: string; inputs: AiFaqRecipeInput[] }
export interface AiFaqRecipe { steps: AiFaqRecipeStep[]; guidance: string | null }

export interface AiFaqRecipeStepResult {
  stepId: string
  tool: string
  args: Record<string, string>
  ok: boolean
  mode: 'real' | 'fake' | 'skipped'
  output: string | null
  problem: string | null
}

export interface AiFaqRecipeTestResult { success: boolean; problems: string[]; steps: AiFaqRecipeStepResult[] }

/** Chamar o MCP de verdade (só consultas) e os retornos fingidos por passo. */
export interface AiFaqRunOptions { chamarDeVerdade: boolean; fingidos: Record<string, string> }

export const MAX_RECIPE_STEPS = 5

/** Próximo id livre ("p1", "p2"…): o encadeamento aponta pro id, então ele não muda quando o passo troca de lugar. */
export function nextStepId(recipe: AiFaqRecipe): string {
  let n = recipe.steps.length + 1
  while (recipe.steps.some(s => s.id === `p${n}`)) n++
  return `p${n}`
}

/** Entradas do passo pra uma ferramenta: mantém o que já estava configurado pro mesmo nome; obrigatória nova = perguntar. */
export function inputsFor(tool: McpTool, current: AiFaqRecipeInput[] = []): AiFaqRecipeInput[] {
  return tool.params.map(p => current.find(i => i.name === p.name)
    ?? { name: p.name, source: p.required ? 'ask' : 'none', description: p.required ? (p.description ?? '') : null })
}

/** O que o agente vai perguntar ao contato (um por nome). */
export function askInputs(recipe: AiFaqRecipe): AiFaqRecipeInput[] {
  const seen = new Set<string>()
  return recipe.steps.flatMap(s => s.inputs).filter(i => i.source === 'ask' && !seen.has(i.name) && seen.add(i.name))
}

/** Tira o passo e solta as entradas que dependiam dele (viram "perguntar"). */
export function removeStep(recipe: AiFaqRecipe, id: string): AiFaqRecipe {
  return {
    ...recipe,
    steps: recipe.steps.filter(s => s.id !== id).map(s => ({
      ...s,
      inputs: s.inputs.map(i => (i.source === 'step' && i.stepId === id ? { name: i.name, source: 'ask', description: '' } : i)),
    })),
  }
}

/** Sobe/desce um passo; entrada que passaria a apontar pra um passo DEPOIS dela é solta (vira "perguntar"). */
export function moveStep(recipe: AiFaqRecipe, id: string, delta: -1 | 1): AiFaqRecipe {
  const index = recipe.steps.findIndex(s => s.id === id)
  const target = index + delta
  if (index < 0 || target < 0 || target >= recipe.steps.length) return recipe
  const steps = [...recipe.steps]
  ;[steps[index], steps[target]] = [steps[target], steps[index]]
  return {
    ...recipe,
    steps: steps.map((s, i) => ({
      ...s,
      inputs: s.inputs.map(input => (input.source === 'step' && !steps.slice(0, i).some(p => p.id === input.stepId)
        ? { name: input.name, source: 'ask', description: '' }
        : input)),
    })),
  }
}

export async function listAiFaqTools(token: string): Promise<AiFaqToolCatalog> {
  try {
    const res = await fetch(`${WOOTDASH_BASE}/ia/duvidas/ferramentas`, { headers: headers(token) })
    if (!res.ok) return { tools: [], problem: res.status === 403 ? 'Sem permissão (só o perfil Sistema).' : `Erro ${res.status} no servidor.` }
    return await res.json()
  } catch {
    return { tools: [], problem: 'Não foi possível falar com o servidor.' }
  }
}

export const saveAiFaqRecipe = (token: string, id: string, receita: AiFaqRecipe | null) =>
  action(token, 'PUT', `${id}/receita`, { receita })

export const testAiFaqRecipe = (token: string, receita: AiFaqRecipe, valores: Record<string, string>, opcoes: AiFaqRunOptions) =>
  post<AiFaqRecipeTestResult>(token, 'receita/testar', { receita, valores, ...opcoes },
    problem => ({ success: false, problems: [problem], steps: [] }), hasSuccess)

/* ── Mesclagem inteligente: a IA diz quais dúvidas são a mesma intenção ── */

export interface AiFaqMergeItem { id: string; question: string; count: number; status: AiFaqStatus; hasAnswer: boolean; hasRecipe: boolean; inMenu: boolean }

/** Juntar sourceIds em targetId com o texto question. warning = algo pra conferir antes (respostas diferentes). */
export interface AiFaqMergeProposal { targetId: string; sourceIds: string[]; question: string; items: AiFaqMergeItem[]; totalCount: number; warning: string | null }

export interface AiFaqMergeProposals { success: boolean; problem: string | null; proposals: AiFaqMergeProposal[]; analyzed: number }

export const suggestAiFaqMerges = (token: string, dias: AiFaqPeriod) =>
  post<AiFaqMergeProposals>(token, 'mesclagem-inteligente', { dias },
    problem => ({ success: false, problem, proposals: [], analyzed: 0 }), hasSuccess)
